import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';
import ReferralTracker from './components/ReferralTracker';

// Public Pages
import EnhancedSEOOptimizedHomePage from './pages/enhanced-seo-optimized-home-page';
import MaritimeTransportBlogHub from './pages/maritime-transport-blog-hub';
import LogisticsDigitalCockpitArticle from './pages/logistics-digital-cockpit-article';
import MaritimeTransportBlogArticle from './pages/maritime-transport-blog-article';
import ParticuliersLandingPage from './pages/particuliers-landing-page';
import ProfessionalServicesLandingPage from './pages/professional-services-landing-page';
import DedicatedPricingPage from './pages/dedicated-pricing-page';
import ShippingInformation from './pages/shipping-information';
import PublicMaritimeOrderPage from './pages/public-maritime-order-page';
import HomeTracking from './pages/home-tracking';
import ShipmentTrackingTimeline from './pages/shipment-tracking-timeline';
import BarrelOrderTracking from './pages/barrel-order-tracking';
import MentionsLegales from './pages/mentions-legales';
import CGV from './pages/cgv';
import PolitiqueDeConfidentialite from './pages/politique-de-confidentialite';

// Client Pages
import ClientLogin from './pages/client-login';
import ClientRegistration from './pages/client-registration';
import ClientPasswordReset from './pages/client-password-reset';
import ClientDashboard from './pages/client-dashboard';
import ClientShipmentDetail from './pages/client-shipment-detail';
import ClientProfile from './pages/client-profile';
import ResetPassword from './pages/reset-password';

// Professional Client Pages
import ProfessionalClientLogin from './pages/professional-client-login';
import ProfessionalClientRegister from './pages/professional-client-register';
import ProfessionalClientDashboardV2 from './pages/professional-client-dashboard-v2';

// Admin Password Reset Pages
import AdminPasswordReset from './pages/admin-password-reset';

import UpdatePassword from './pages/update-password';
import ForgotPassword from './pages/forgot-password';
import ChangePassword from './pages/change-password';

// Admin Pages
import AdminLogin from './pages/admin-login';
import ComprehensiveAdminKPIDashboard from './pages/comprehensive-admin-kpi-dashboard';
import AdminShipmentsDashboard from './pages/admin-shipments-dashboard';
import ShipmentDetailManagement from './pages/shipment-detail-management';
import AdminShipmentDetailView from './pages/admin-shipment-detail-view';
import CreateNewShipment from './pages/create-new-shipment';
import AdminClients from './pages/admin-clients';
import AdminClientDetail from './pages/admin-client-detail';
import CustomerManagementSystem from './pages/customer-management-system';
import ContainerManagementDashboard from './pages/container-management-dashboard';
import AdminContainersManagement from './pages/admin-containers-dashboard';
import AdminContainerDetail from './pages/admin-container-detail';
import ContainerDetailManagement from './pages/container-detail-management';
import EmployeeManagementDashboard from './pages/employee-management-dashboard';
import PickupTaskPlanningSystem from './pages/pickup-task-planning-system';
import WhatsAppNotificationsManagement from './pages/whats-app-notifications-management';
import WhatsAppConfigurationManagement from './pages/whats-app-configuration-management';
import NotificationLogsAnalytics from './pages/notification-logs-analytics';
import AdminNotifications from './pages/admin-notifications';
import N8nWebhookMonitoringDashboard from './pages/n8n-webhook-monitoring-dashboard';
import UniversalAPIEndpointConfiguration from './pages/universal-api-endpoint-configuration';
import SEOManagementDashboard from './pages/seo-management-dashboard';
import TechnicalSEOPerformanceAnalyzer from './pages/technical-seo-performance-analyzer';
import KeywordResearchOptimizationCenter from './pages/keyword-research-optimization-center';
import OpenAIQuoteHistoryManagement from './pages/open-ai-quote-history-management';
import ProfessionalClientsManagementDashboard from './pages/professional-clients-management-dashboard';
import DetailedProfessionalClientProfile from './pages/detailed-professional-client-profile';
import ProfessionalOrdersProcessingCenter from './pages/professional-orders-processing-center';
import FatouAIConfigurationManagement from './pages/fatou-ai-configuration-management';
import FatouAIInteractionMonitoringDashboard from './pages/fatou-ai-interaction-monitoring-dashboard';
import FatouAIKnowledgeBaseRefinementCenter from './pages/fatou-ai-knowledge-base-refinement-center';
import FatouAIReservationCaptureSystem from './pages/fatou-ai-reservation-capture-system';
import AdminFatouReservationsDashboard from './pages/admin-fatou-reservations-dashboard';
import AutomatedReservationConfirmationSystem from './pages/automated-reservation-confirmation-system';
import CentralizedBarrelInventoryManagementSystem from './pages/centralized-barrel-inventory-management-system';
import AutomatedReplenishmentControlCenter from './pages/automated-replenishment-control-center';
import BarrelInventoryAnalyticsCenter from './pages/barrel-inventory-analytics-center';
import BarrelStockAnalyticsDashboard from './pages/barrel-stock-analytics-dashboard';
import EmptyBarrelsSalesManagement from './pages/empty-barrels-sales-management';
import BarrelCustomerManagementDashboard from './pages/barrel-customer-management-dashboard';
import BarrelCustomerOrdersManagement from './pages/barrel-customer-orders-management';
import BarrelDepositPlanningSystem from './pages/barrel-deposit-planning-system';
import BarrelDepositSchedulingSystem from './pages/barrel-deposit-scheduling-system';
import PromoSummerOrders from './pages/promo-summer-orders';
import PartnerAccounting from './pages/partner-accounting';

import CartonInventoryManagement from './pages/carton-inventory-management';
import CartonSuppliersManagement from './pages/carton-suppliers-management';
import CartonSalesManagement from './pages/carton-sales-management';
import TruckLoadingManagement from './pages/truck-loading-management';

import UnifiedSalesManagement from './pages/unified-sales-management';
import UnifiedInventoryManagement from './pages/unified-inventory-management';
import UnifiedStockAnalytics from './pages/unified-stock-analytics';
import UnifiedReplenishmentCenter from './pages/unified-replenishment-center';

// RBAC Pages
import AdminUsersPermissions from './pages/admin-users-permissions';
import SecurityJournal from './pages/security-journal';
import AdminUsersDiagnostic from './pages/admin-users-diagnostic';
import AdminWhatsApp from './pages/admin-whatsapp';
import AdminFatouConversations from './pages/admin-fatou-conversations';

// Agent Abidjan Pages
import AgentAbidjanLogin from './pages/agent-abidjan-login';
import DashboardAgentAbidjan from './pages/dashboard-agent-abidjan';
import AbidjanContainerDistributionDashboard from './pages/abidjan-container-distribution-dashboard';
import AbidjanContainerDetailManagement from './pages/abidjan-container-detail-management';
import AbidjanDeliveryIncidentsManagement from './pages/abidjan-delivery-incidents-management';

// Driver Pages
import DriverLogin from './pages/driver-login';
import DriverDashboard from './pages/driver-dashboard';
import DriverPersonalDashboard from './pages/driver-personal-dashboard';
import DriverPickupDetailWithIntegratedSignature from './pages/driver-pickup-detail-with-integrated-signature';
import DriverRouteSheetMobileSignature from './pages/driver-route-sheet-mobile-signature';

// Protected Route Components
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ProtectedAgentRoute from './components/ProtectedAgentRoute';
import ProtectedDriverRoute from './components/ProtectedDriverRoute';
import ProtectedAdminOrAgentRoute from './components/ProtectedAdminOrAgentRoute';
import ProtectedProfessionalRoute from './components/ProtectedProfessionalRoute';
import ProtectedSuperAdminRoute from './components/ProtectedSuperAdminRoute';

// 404
import NotFound from './pages/NotFound';

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <ReferralTracker />
        <RouterRoutes>
          {/* ================= PUBLIC ROUTES ================= */}
          <Route path="/" element={<EnhancedSEOOptimizedHomePage />} />

          {/* BLOG */}
          <Route path="/blog" element={<MaritimeTransportBlogHub />} />
          <Route path="/maritime-transport-blog-hub" element={<Navigate to="/blog" replace />} />
          <Route path="/blog/logistique-cockpit-digital" element={<LogisticsDigitalCockpitArticle />} />
          <Route path="/maritime-transport-blog-article" element={<MaritimeTransportBlogArticle />} />

          {/* SEGMENTS */}
          <Route path="/particuliers" element={<ParticuliersLandingPage />} />
          <Route path="/professionnels" element={<ProfessionalServicesLandingPage />} />

          {/* ANCIENNES URLS REDIRIGÉES */}
          <Route path="/espace-professionnel" element={<Navigate to="/professionnels" replace />} />
          <Route path="/professional-services-landing-page" element={<Navigate to="/professionnels" replace />} />

          {/* TARIFS */}
          <Route path="/tarifs" element={<DedicatedPricingPage />} />
          <Route path="/dedicated-pricing-page" element={<Navigate to="/tarifs" replace />} />

          {/* INFORMATIONS */}
          <Route path="/shipping-information" element={<ShippingInformation />} />

          {/* MENTIONS LÉGALES */}
          <Route path="/mentions-legales" element={<MentionsLegales />} />

          {/* CGV */}
          <Route path="/cgv" element={<CGV />} />

          {/* POLITIQUE DE CONFIDENTIALITÉ */}
          <Route path="/politique-de-confidentialite" element={<PolitiqueDeConfidentialite />} />

          {/* COMMANDE */}
          <Route path="/commander" element={<PublicMaritimeOrderPage />} />

          {/* SUIVI COMMANDE BARRIQUE */}
          <Route path="/suivi-commande-barrique" element={<BarrelOrderTracking />} />

          {/* TRACKING */}
          <Route path="/home-tracking" element={<HomeTracking />} />
          <Route path="/tracking/:reference" element={<ShipmentTrackingTimeline />} />
          <Route path="/shipment-tracking-timeline/:reference" element={<ShipmentTrackingTimeline />} />

          {/* ================= CLIENT ROUTES ================= */}
          <Route path="/client-login" element={<ClientLogin />} />
          <Route path="/client-registration" element={<ClientRegistration />} />
          <Route path="/client-password-reset" element={<ClientPasswordReset />} />
                    <Route path="/client-forgot-password" element={<Navigate to="/client-password-reset" replace />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/client-dashboard" element={<ClientDashboard />} />
          <Route path="/client-shipment-detail/:shipmentId" element={<ClientShipmentDetail />} />
          <Route path="/client-profile" element={<ClientProfile />} />

          {/* ================= PROFESSIONAL CLIENT ROUTES ================= */}
          <Route path="/professional-client-login" element={<ProfessionalClientLogin />} />
          <Route path="/professional-client-register" element={<ProfessionalClientRegister />} />
          <Route
            path="/professional-client-dashboard/*"
            element={
              <ProtectedProfessionalRoute>
                <ProfessionalClientDashboardV2 />
              </ProtectedProfessionalRoute>
            }
          />
          {/* Alias routes for professional dashboard */}
          <Route
            path="/professional-dashboard/*"
            element={
              <ProtectedProfessionalRoute>
                <ProfessionalClientDashboardV2 />
              </ProtectedProfessionalRoute>
            }
          />

          {/* ================= ADMIN ROUTES ================= */}
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/password-reset" element={<AdminPasswordReset />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedAdminRoute>
                <ComprehensiveAdminKPIDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-shipments-dashboard"
            element={
              <ProtectedAdminRoute>
                <AdminShipmentsDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/shipment-detail-management/:shipmentId"
            element={
              <ProtectedAdminRoute>
                <ShipmentDetailManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-shipment-detail-view/:shipmentId"
            element={
              <ProtectedAdminRoute>
                <AdminShipmentDetailView />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/create-new-shipment"
            element={
              <ProtectedAdminRoute>
                <CreateNewShipment />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-clients"
            element={
              <ProtectedAdminRoute>
                <AdminClients />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-client-detail/:clientId"
            element={
              <ProtectedAdminRoute>
                <AdminClientDetail />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/customer-management-system"
            element={
              <ProtectedAdminRoute>
                <CustomerManagementSystem />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/container-management-dashboard"
            element={
              <ProtectedAdminRoute>
                <ContainerManagementDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-containers-dashboard"
            element={
              <ProtectedAdminRoute>
                <AdminContainersManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-container-detail/:containerId"
            element={
              <ProtectedAdminRoute>
                <AdminContainerDetail />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/container-detail-management/:containerId"
            element={
              <ProtectedAdminRoute>
                <ContainerDetailManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/employee-management-dashboard"
            element={
              <ProtectedAdminRoute>
                <EmployeeManagementDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/pickup-task-planning-system"
            element={
              <ProtectedAdminRoute>
                <PickupTaskPlanningSystem />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/whats-app-notifications-management"
            element={
              <ProtectedAdminRoute>
                <WhatsAppNotificationsManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/whats-app-configuration-management"
            element={
              <ProtectedAdminRoute>
                <WhatsAppConfigurationManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/notification-logs-analytics"
            element={
              <ProtectedAdminRoute>
                <NotificationLogsAnalytics />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-notifications"
            element={
              <ProtectedAdminRoute>
                <AdminNotifications />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/n8n-webhook-monitoring-dashboard"
            element={
              <ProtectedAdminRoute>
                <N8nWebhookMonitoringDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/universal-api-endpoint-configuration"
            element={
              <ProtectedAdminRoute>
                <UniversalAPIEndpointConfiguration />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/seo-management-dashboard"
            element={
              <ProtectedAdminRoute>
                <SEOManagementDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/technical-seo-performance-analyzer"
            element={
              <ProtectedAdminRoute>
                <TechnicalSEOPerformanceAnalyzer />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/keyword-research-optimization-center"
            element={
              <ProtectedAdminRoute>
                <KeywordResearchOptimizationCenter />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/open-ai-quote-history-management"
            element={
              <ProtectedAdminRoute>
                <OpenAIQuoteHistoryManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/professional-clients-management-dashboard"
            element={
              <ProtectedAdminRoute>
                <ProfessionalClientsManagementDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/detailed-professional-client-profile/:clientId"
            element={
              <ProtectedAdminRoute>
                <DetailedProfessionalClientProfile />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/professional-orders-processing-center"
            element={
              <ProtectedAdminRoute>
                <ProfessionalOrdersProcessingCenter />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/fatou-ai-configuration-management"
            element={
              <ProtectedAdminRoute>
                <FatouAIConfigurationManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/fatou-ai-interaction-monitoring-dashboard"
            element={
              <ProtectedAdminRoute>
                <FatouAIInteractionMonitoringDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/fatou-ai-knowledge-base-refinement-center"
            element={
              <ProtectedAdminRoute>
                <FatouAIKnowledgeBaseRefinementCenter />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/fatou-ai-reservation-capture-system"
            element={
              <ProtectedAdminRoute>
                <FatouAIReservationCaptureSystem />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin-fatou-reservations-dashboard"
            element={
              <ProtectedAdminRoute>
                <AdminFatouReservationsDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/automated-reservation-confirmation-system"
            element={
              <ProtectedAdminRoute>
                <AutomatedReservationConfirmationSystem />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/centralized-barrel-inventory-management-system"
            element={
              <ProtectedAdminRoute>
                <CentralizedBarrelInventoryManagementSystem />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/automated-replenishment-control-center"
            element={
              <ProtectedAdminRoute>
                <AutomatedReplenishmentControlCenter />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/barrel-inventory-analytics-center"
            element={
              <ProtectedAdminRoute>
                <BarrelInventoryAnalyticsCenter />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/barrel-stock-analytics-dashboard"
            element={
              <ProtectedAdminRoute>
                <BarrelStockAnalyticsDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/empty-barrels-sales-management"
            element={
              <ProtectedAdminRoute>
                <EmptyBarrelsSalesManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/barrel-customer-management-dashboard"
            element={
              <ProtectedAdminRoute>
                <BarrelCustomerManagementDashboard />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/barrel-customer-orders-management"
            element={
              <ProtectedAdminRoute>
                <BarrelCustomerOrdersManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/barrel-deposit-planning-system"
            element={
              <ProtectedAdminRoute>
                <BarrelDepositPlanningSystem />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/barrel-deposit-scheduling-system"
            element={
              <ProtectedAdminRoute>
                <BarrelDepositSchedulingSystem />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/promo-summer-orders"
            element={
              <ProtectedAdminRoute>
                <PromoSummerOrders />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/partner-accounting"
            element={
              <ProtectedAdminRoute>
                <PartnerAccounting />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/carton-inventory-management"
            element={
              <ProtectedAdminRoute>
                <CartonInventoryManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/carton-suppliers-management"
            element={
              <ProtectedAdminRoute>
                <CartonSuppliersManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/carton-sales-management"
            element={
              <ProtectedAdminRoute>
                <CartonSalesManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/truck-loading-management"
            element={
              <ProtectedAdminRoute>
                <TruckLoadingManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/unified-sales-management"
            element={
              <ProtectedAdminRoute>
                <UnifiedSalesManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/unified-inventory-management"
            element={
              <ProtectedAdminRoute>
                <UnifiedInventoryManagement />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/unified-stock-analytics"
            element={
              <ProtectedAdminRoute>
                <UnifiedStockAnalytics />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/unified-replenishment-center"
            element={
              <ProtectedAdminRoute>
                <UnifiedReplenishmentCenter />
              </ProtectedAdminRoute>
            }
          />

          {/* ================= RBAC / ADMINISTRATION ROUTES ================= */}
          <Route
            path="/admin-users-permissions"
            element={
              <ProtectedSuperAdminRoute>
                <AdminUsersPermissions />
              </ProtectedSuperAdminRoute>
            }
          />
          <Route
            path="/security-journal"
            element={
              <ProtectedSuperAdminRoute>
                <SecurityJournal />
              </ProtectedSuperAdminRoute>
            }
          />
          <Route
            path="/admin-users-diagnostic"
            element={
              <ProtectedSuperAdminRoute>
                <AdminUsersDiagnostic />
              </ProtectedSuperAdminRoute>
            }
          />
          <Route
            path="/admin/whatsapp"
            element={
              <ProtectedAdminRoute>
                <AdminWhatsApp />
              </ProtectedAdminRoute>
            }
          />
          <Route
            path="/admin/fatou-conversations"
            element={
              <ProtectedAdminRoute>
                <AdminFatouConversations />
              </ProtectedAdminRoute>
            }
          />

          {/* ================= AGENT ABIDJAN ROUTES ================= */}
          <Route path="/agent-abidjan-login" element={<AgentAbidjanLogin />} />
          <Route
            path="/dashboard-agent-abidjan"
            element={
              <ProtectedAgentRoute>
                <DashboardAgentAbidjan />
              </ProtectedAgentRoute>
            }
          />
          <Route
            path="/abidjan-container-distribution-dashboard"
            element={
              <ProtectedAdminOrAgentRoute>
                <AbidjanContainerDistributionDashboard />
              </ProtectedAdminOrAgentRoute>
            }
          />
          <Route
            path="/abidjan-container-detail-management/:containerId"
            element={
              <ProtectedAdminOrAgentRoute>
                <AbidjanContainerDetailManagement />
              </ProtectedAdminOrAgentRoute>
            }
          />
          <Route
            path="/abidjan-delivery-incidents-management"
            element={
              <ProtectedAgentRoute>
                <AbidjanDeliveryIncidentsManagement />
              </ProtectedAgentRoute>
            }
          />

          {/* ================= DRIVER ROUTES ================= */}
          <Route path="/driver-login" element={<DriverLogin />} />
          <Route
            path="/driver-dashboard"
            element={
              <ProtectedDriverRoute>
                <DriverDashboard />
              </ProtectedDriverRoute>
            }
          />
          <Route
            path="/driver-personal-dashboard"
            element={
              <ProtectedDriverRoute>
                <DriverPersonalDashboard />
              </ProtectedDriverRoute>
            }
          />
          <Route
            path="/driver-pickup-detail-with-integrated-signature/:pickupId"
            element={
              <ProtectedDriverRoute>
                <DriverPickupDetailWithIntegratedSignature />
              </ProtectedDriverRoute>
            }
          />
          <Route
            path="/driver-route-sheet-mobile-signature"
            element={
              <ProtectedDriverRoute>
                <DriverRouteSheetMobileSignature />
              </ProtectedDriverRoute>
            }
          />

          {/* ================= 404 ================= */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;